import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Plus, Edit, Trash2, Loader2, Filter, X } from 'lucide-react';
import { Subject } from '../../types';
import { api } from '../../utils/api';
import { normalizeAcademicYear } from '../../utils/academicYear';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

export function SubjectManagement({ globalSemester, globalAcademicYear }: { globalSemester?: number; globalAcademicYear?: string }) {
  const { accessToken } = useAuth();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [filterSemester, setFilterSemester] = useState<string>(globalSemester !== undefined ? globalSemester.toString() : 'all');
  const [filterProgram, setFilterProgram] = useState<string>('all');
  const [filterCurriculumVersion, setFilterCurriculumVersion] = useState<string>(globalAcademicYear || 'all');
  const [filterYearLevel, setFilterYearLevel] = useState<string>('all');
  const [filterCode, setFilterCode] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    units: 3,
    courseId: '',
    curriculumVersion: globalAcademicYear || '',
    yearLevel: 1,
    semester: 1,
    prerequisites: [] as string[]
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (globalSemester !== undefined) {
      setFilterSemester(globalSemester.toString());
    }
  }, [globalSemester]);

  useEffect(() => {
    if (globalAcademicYear) {
      setFilterCurriculumVersion(globalAcademicYear);
      setFormData((prev) => ({
        ...prev,
        curriculumVersion: prev.curriculumVersion || globalAcademicYear
      }));
    }
  }, [globalAcademicYear]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = accessToken || '';
      const [subjectsData, coursesData] = await Promise.all([
        api.getSubjects(token),
        api.getCourses(token)
      ]);
      setSubjects(subjectsData.subjects);
      setCourses(coursesData.courses);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      const token = accessToken || '';
      const payload = {
        ...formData,
        curriculumVersion: normalizeAcademicYear(formData.curriculumVersion) || undefined
      };

      if (editingSubject) {
        await api.updateSubject(editingSubject.id, payload, token);
        toast.success('Course updated successfully');
      } else {
        await api.createSubject(payload, token);
        toast.success('Course created successfully');
      }

      await fetchData();
      handleDialogClose();
    } catch (error) {
      console.error('Error saving course:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save course');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (subject: Subject) => {
    setEditingSubject(subject);
    setFormData({
      code: subject.code,
      name: subject.name,
      description: subject.description,
      units: subject.units,
      courseId: subject.courseId || '',
      curriculumVersion: subject.curriculumVersion || '',
      yearLevel: subject.yearLevel ?? 1,
      semester: typeof subject.semester === 'string' ? parseInt(subject.semester, 10) : (subject.semester || 1),
      prerequisites: subject.prerequisites || []
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (subjectId: string) => {
    if (!confirm('Are you sure you want to delete this course?')) {
      return;
    }

    try {
      const token = accessToken || '';
      await api.deleteSubject(subjectId, token);
      toast.success('Course deleted successfully');
      await fetchData();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingSubject(null);
    setFormData({
      code: '',
      name: '',
      description: '',
      units: 3,
      courseId: '',
      curriculumVersion: globalAcademicYear || '',
      yearLevel: 1,
      semester: 1,
      prerequisites: []
    });
  };

  const getCourseCode = (courseId: string) => {
    const course = courses.find((item) => item.id === courseId);
    return course?.code || 'Unknown';
  };

  const getCourseCurriculumVersions = (courseId: string) => {
    const course = courses.find((item) => item.id === courseId);
    return course?.curriculumVersions || [];
  };

  const getYearLevelLabel = (yearLevel?: number) => {
    if (yearLevel === 0) return 'All Year Levels';

    const labels: Record<number, string> = {
      1: '1st Year',
      2: '2nd Year',
      3: '3rd Year',
    };

    return labels[yearLevel || 1] || `Year ${yearLevel}`;
  };

  const selectedCourseCurriculumVersions = getCourseCurriculumVersions(formData.courseId);
  const subjectFilterCurriculumOptions = Array.from(new Set([
    ...courses.flatMap((course) => course.curriculumVersions || []),
    ...subjects.map((subject) => subject.curriculumVersion).filter((value): value is string => Boolean(value)),
    globalAcademicYear || ''
  ].filter(Boolean))).sort((left, right) => left.localeCompare(right));

  const filteredSubjects = subjects.filter((subject) => {
    if (filterSemester !== 'all' && String(subject.semester) !== filterSemester) return false;
    if (filterProgram !== 'all' && subject.courseId !== filterProgram) return false;
    if (filterCurriculumVersion !== 'all' && (subject.curriculumVersion || '') !== filterCurriculumVersion) return false;
    if (filterYearLevel !== 'all' && String(subject.yearLevel) !== filterYearLevel) return false;
    if (filterCode && !subject.code.toLowerCase().includes(filterCode.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredSubjects.length / rowsPerPage));
  const pageStartIndex = (currentPage - 1) * rowsPerPage;
  const paginatedSubjects = filteredSubjects.slice(pageStartIndex, pageStartIndex + rowsPerPage);
  const paginationStart = filteredSubjects.length === 0 ? 0 : pageStartIndex + 1;
  const paginationEnd = filteredSubjects.length === 0
    ? 0
    : Math.min(pageStartIndex + rowsPerPage, filteredSubjects.length);

  const hasActiveFilters = filterSemester !== 'all' || filterProgram !== 'all' || filterCurriculumVersion !== 'all' || filterYearLevel !== 'all' || filterCode !== '';

  const clearFilters = () => {
    setFilterSemester('all');
    setFilterProgram('all');
    setFilterCurriculumVersion('all');
    setFilterYearLevel('all');
    setFilterCode('');
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSemester, filterProgram, filterCurriculumVersion, filterYearLevel, filterCode, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }

    setCurrentPage(page);
  };

  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
  };

  const visiblePages = getVisiblePages();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Course Management</CardTitle>
            <CardDescription>Manage academic courses</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Subject
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingSubject ? 'Edit Course' : 'Add New Course'}
                </DialogTitle>
                <DialogDescription>
                  {editingSubject
                    ? 'Update the course information below'
                    : 'Fill in the details to create a new academic course'
                  }
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Course Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({...formData, code: e.target.value})}
                      placeholder="e.g., MCS 201"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="units">Units</Label>
                    <Input
                      id="units"
                      type="number"
                      value={formData.units}
                      onChange={(e) => setFormData({...formData, units: parseInt(e.target.value)})}
                      min="1"
                      max="6"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Course Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g., Database Management Systems"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <div className="space-y-2">
                    <Label htmlFor="course">Course</Label>
                    <Select
                      value={formData.courseId}
                      onValueChange={(value) => setFormData((prev) => ({
                        ...prev,
                        courseId: value,
                        curriculumVersion: getCourseCurriculumVersions(value).includes(prev.curriculumVersion)
                          ? prev.curriculumVersion
                          : (globalAcademicYear && getCourseCurriculumVersions(value).includes(globalAcademicYear)
                            ? globalAcademicYear
                            : '')
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            {course.code} - {course.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="curriculumVersion">Curriculum AY</Label>
                    <Select
                      value={formData.curriculumVersion || 'none'}
                      onValueChange={(value) => setFormData({...formData, curriculumVersion: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select curriculum year" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not versioned</SelectItem>
                        {selectedCourseCurriculumVersions.map((version) => (
                          <SelectItem key={version} value={version}>
                            {version}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="yearLevel">Year Level</Label>
                    <Select value={formData.yearLevel.toString()} onValueChange={(value) => setFormData({...formData, yearLevel: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">All Year Levels</SelectItem>
                        <SelectItem value="1">1st Year</SelectItem>
                        <SelectItem value="2">2nd Year</SelectItem>
                        <SelectItem value="3">3rd Year</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="semester">Semester</Label>
                    <Select value={formData.semester.toString()} onValueChange={(value) => setFormData({...formData, semester: parseInt(value)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1st Semester</SelectItem>
                        <SelectItem value="2">2nd Semester</SelectItem>
                        <SelectItem value="3">Summer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Course description..."
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={handleDialogClose} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editingSubject ? 'Update' : 'Add'} Course
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {!loading && subjects.length > 0 && (
          <div className="mb-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 px-2 text-xs">
                  <X className="mr-1 h-3 w-3" />
                  Clear
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="space-y-1">
                <Label className="text-xs">Semester</Label>
                <Select value={filterSemester} onValueChange={setFilterSemester}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Semesters" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Semesters</SelectItem>
                    <SelectItem value="1">1st Semester</SelectItem>
                    <SelectItem value="2">2nd Semester</SelectItem>
                    <SelectItem value="3">Summer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Program</Label>
                <Select value={filterProgram} onValueChange={setFilterProgram}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Programs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Programs</SelectItem>
                    {courses.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Year Level</Label>
                <Select value={filterYearLevel} onValueChange={setFilterYearLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Year Levels" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Year Levels</SelectItem>
                    <SelectItem value="1">1st Year</SelectItem>
                    <SelectItem value="2">2nd Year</SelectItem>
                    <SelectItem value="3">3rd Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Curriculum AY</Label>
                <Select value={filterCurriculumVersion} onValueChange={setFilterCurriculumVersion}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Curriculum Versions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Curriculum Versions</SelectItem>
                    {subjectFilterCurriculumOptions.map((version) => (
                      <SelectItem key={version} value={version}>
                        {version}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Course Code</Label>
                <Input
                  placeholder="Search by code..."
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value)}
                />
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : subjects.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No courses found. Add your first course to get started.
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No courses match the selected filters.
          </div>
        ) : (
          <>
            <ScrollArea className="w-full" type="auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead className="min-w-[200px]">Name</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Curriculum AY</TableHead>
                    <TableHead>Year/Sem</TableHead>
                    <TableHead>Units</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedSubjects.map((subject) => (
                    <TableRow key={subject.id}>
                      <TableCell>
                        <Badge variant="outline">{subject.code}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[250px] whitespace-normal break-words font-medium">{subject.name}</TableCell>
                      <TableCell>{getCourseCode(subject.courseId || '')}</TableCell>
                      <TableCell>
                        {subject.curriculumVersion ? (
                          <Badge variant="outline">{subject.curriculumVersion}</Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">Legacy</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-1">
                          <Badge variant="secondary">{getYearLevelLabel(subject.yearLevel)}</Badge>
                          <Badge variant="secondary">S{subject.semester}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>{subject.units} units</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(subject)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(subject.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {paginationStart}-{paginationEnd} of {filteredSubjects.length} courses
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground">Rows per page</Label>
                  <Select value={String(rowsPerPage)} onValueChange={(value) => setRowsPerPage(Number(value))}>
                    <SelectTrigger className="w-[88px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Pagination className="justify-end sm:justify-center">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handlePageChange(currentPage - 1);
                        }}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                    {visiblePages.map((page, index) => {
                      const previousPage = visiblePages[index - 1];
                      const shouldShowEllipsis = previousPage !== undefined && page - previousPage > 1;

                      return (
                        <div key={page} className="flex items-center">
                          {shouldShowEllipsis && (
                            <PaginationItem>
                              <PaginationEllipsis />
                            </PaginationItem>
                          )}
                          <PaginationItem>
                            <PaginationLink
                              href="#"
                              isActive={page === currentPage}
                              onClick={(event) => {
                                event.preventDefault();
                                handlePageChange(page);
                              }}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        </div>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          handlePageChange(currentPage + 1);
                        }}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
